/**
 * useCoachProfile — Coach Profile orchestrator
 *
 * Wires the data layer (buildProfile + diffProfile + storeProfile) into the
 * React app:
 *
 *   1. On mount, reads the stored profile from disk so the dashboard has
 *      something to show immediately (zero-flicker).
 *   2. Exposes a `rebuild()` async function the dashboard's "Refresh" button
 *      calls. Rebuild iterates every setup + every ATM with enough trade
 *      history, runs `coach.analyze` for each, accumulates the observations,
 *      builds a fresh profile, and persists it (which auto-pushes the prior
 *      to history).
 *   3. Exposes a `clearMemory()` async function for the "Reset Coach memory"
 *      affordance — wipes stored profile + history.
 *   4. Computes the diff between the prior snapshot and current profile so
 *      the dashboard can render "Since last snapshot…" copy.
 *
 * Designed to be called once at the TradingJournal level. The dashboard
 * subscribes to the returned state via prop-drilling.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import coach from '../services/coach';
import { buildProfile } from '../services/coach/profile/buildProfile';
import { diffProfile } from '../services/coach/profile/diffProfile';
import { readStoredProfile, writeStoredProfile, clearStoredProfile } from '../services/coach/profile/storeProfile';
import { setRuntimeContext, clearRuntimeContext } from '../services/coach/profile/runtimeContext';
import { getCurrentTradingDay } from '../services/timeService';

// Setup/ATM scopes need at least this many trades to produce useful
// observations. Below this we skip the scope entirely so the profile isn't
// padded with low-confidence results from underpowered samples.
const MIN_TRADES_PER_SCOPE = 6;

const useCoachProfile = ({
  // The data the Coach scopes need — same shape as the SetupsTab + AtmTab
  // already pass into coach.analyze. The hook just iterates and aggregates.
  closedFilteredTrades = [],
  setupAtmBaseTrades = [],
  allConfluences = [],
  allMistakes = [],
  setupDetails = {},
  atmDetails = {},
  ntTemplateVersions = {},
  // Phase 17 — symbol → {tickSize, tickValue, pointValue} lookup. Required
  // by atmVersionComparison's per-bracket attribution (matchExitsToBrackets).
  futuresSpecs = {},
  allJournalEntries = {},
  getTradeNetPnL,
  getAccountById,
  // Optional: if the host has a tier setting (free/pro), pass it through.
  tier = 'free',
}) => {
  // ── Stored profile state ──
  const [stored, setStored] = useState({ current: null, history: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitialReadRef = useRef(false);
  // The initial-read Promise — kept in a ref so rebuild() can await it
  // before operating. Without this, a user clicking Refresh in the brief
  // window between mount and read-completes would call rebuild() with
  // `stored = { current: null, history: [] }` (the initial state),
  // overwriting the on-disk profile with an empty-history version.
  const initialReadPromiseRef = useRef(null);

  // Initial load — once per mount.
  useEffect(() => {
    let cancelled = false;
    initialReadPromiseRef.current = readStoredProfile()
      .then(result => {
        if (cancelled) return null;
        setStored(result);
        hasInitialReadRef.current = true;
        return result;
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || String(e));
        return { current: null, history: [] };
      });
    return () => { cancelled = true; };
  }, []);

  // Mirror the stored profile into the runtime singleton so scopes
  // (setup/atm/day) can read it during enrichWithTemporalContext without
  // having to thread the profile through every coach.analyze call site.
  // Fires whenever stored changes (initial load, rebuild, clear).
  useEffect(() => {
    setRuntimeContext({ profile: stored.current, history: stored.history });
  }, [stored]);

  // ── Discover the universe of setups + ATMs to iterate ──
  // Use setupAtmBaseTrades so we only include trades the user has chosen to
  // count toward setup/ATM stats (same source the existing tabs use).
  const setupsToAnalyze = useMemo(() => {
    const counts = new Map();
    for (const t of setupAtmBaseTrades) {
      const name = t?.setup;
      if (!name) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return [...counts.entries()]
      .filter(([, n]) => n >= MIN_TRADES_PER_SCOPE)
      .map(([name]) => name);
  }, [setupAtmBaseTrades]);

  const atmsToAnalyze = useMemo(() => {
    const counts = new Map();
    for (const t of setupAtmBaseTrades) {
      const name = t?.atmStrategy;
      if (!name) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return [...counts.entries()]
      .filter(([, n]) => n >= MIN_TRADES_PER_SCOPE)
      .map(([name]) => name);
  }, [setupAtmBaseTrades]);

  // Account scope — same shape but counted by accountId. Account analyzers
  // need a higher floor (8 trades) than setup/ATM (6) because per-account
  // baselines are noisier; honor each analyzer's own internal threshold by
  // sending everything through and letting the analyzer self-skip.
  const accountsToAnalyze = useMemo(() => {
    const counts = new Map();
    for (const t of setupAtmBaseTrades) {
      const id = t?.accountId;
      if (!id) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return [...counts.entries()]
      .filter(([, n]) => n >= MIN_TRADES_PER_SCOPE)
      .map(([id]) => id);
  }, [setupAtmBaseTrades]);

  // ── Rebuild ──
  // Iterates all setup + ATM scopes, runs coach.analyze for each,
  // accumulates the observations, builds a fresh profile, persists it.
  const rebuild = useCallback(async () => {
    if (isLoading) return;  // Prevent concurrent rebuilds

    // ── Wait for initial read before operating (Phase 19.3 follow-up)
    // If the user clicks Refresh in the brief window between mount and
    // the async initial-read settling, `stored` is still the empty
    // default — building a profile from that would write to disk with
    // an empty history, losing the user's prior profile entirely.
    // Awaiting the initial-read Promise resolves before we touch state.
    let baseStored = stored;
    if (!hasInitialReadRef.current && initialReadPromiseRef.current) {
      try {
        const initial = await initialReadPromiseRef.current;
        if (initial) baseStored = initial;
      } catch {
        // Initial read failed — proceed with the empty default. The
        // rebuild itself isn't blocked by a load failure; we just lose
        // prior-history continuity (no worse than a fresh install).
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      const allObservations = [];
      const sharedCtx = {
        anchorDate: getCurrentTradingDay(),
        allConfluences,
        allMistakes,
        setupDetails,
        atmDetails,
        ntTemplateVersions,
        futuresSpecs,  // Phase 17 — used by atmVersionComparison per-bracket attribution
        allJournalEntries,
        getTradeNetPnL,
        getAccountById,
        tier,
      };

      // Setup scope
      for (const setupName of setupsToAnalyze) {
        try {
          const obs = coach.analyze(
            { type: 'setup', id: setupName },
            { ...sharedCtx, allTrades: setupAtmBaseTrades },
          );
          if (Array.isArray(obs)) allObservations.push(...obs);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[useCoachProfile] setup "${setupName}" analyze failed:`, e?.message || e);
        }
      }

      // ATM scope
      for (const atmName of atmsToAnalyze) {
        try {
          const obs = coach.analyze(
            { type: 'atm', id: atmName },
            { ...sharedCtx, allTrades: setupAtmBaseTrades },
          );
          if (Array.isArray(obs)) allObservations.push(...obs);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[useCoachProfile] atm "${atmName}" analyze failed:`, e?.message || e);
        }
      }

      // Account scope — per-account drift + per-account setup spotlights +
      // cross-account asymmetry on shared setups. Analyzer self-skips
      // accounts with too few trades for a meaningful baseline comparison.
      for (const accountId of accountsToAnalyze) {
        try {
          const acct = getAccountById ? getAccountById(accountId) : null;
          const obs = coach.analyze(
            { type: 'account', id: accountId, label: acct?.name || accountId },
            { ...sharedCtx, allTrades: setupAtmBaseTrades },
          );
          if (Array.isArray(obs)) allObservations.push(...obs);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[useCoachProfile] account "${accountId}" analyze failed:`, e?.message || e);
        }
      }

      // The synthesis observations from each scope are intentionally fed
      // into the profile builder along with the underlying observations —
      // buildProfile drops them itself (analyzer key === 'synthesis') so
      // they don't pollute the edge/leak lists, but we don't need to
      // pre-filter here.

      const newProfile = buildProfile({
        observations: allObservations,
        // Use the awaited baseStored — not the closure's `stored` — so
        // rebuilds fired before the initial read see the just-loaded
        // profile, not the empty default. Bug 4.1 fix.
        priorProfile: baseStored.current,
        windowDays: 30,
        tradeCount: setupAtmBaseTrades.length,
      });

      const updated = await writeStoredProfile(newProfile, baseStored);
      setStored(updated);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading, stored,
    setupsToAnalyze, atmsToAnalyze, accountsToAnalyze,
    setupAtmBaseTrades, allConfluences, allMistakes,
    setupDetails, atmDetails, ntTemplateVersions, allJournalEntries,
    getTradeNetPnL, getAccountById, tier,
  ]);

  const clearMemory = useCallback(async () => {
    setIsLoading(true);
    try {
      const cleared = await clearStoredProfile();
      setStored(cleared);
      // Wipe the runtime singleton too so downstream scopes immediately
      // stop computing temporal context against the cleared profile.
      clearRuntimeContext();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Drift between the current profile and the prior snapshot in history.
  // history[0] is the most recent prior; if nothing's there, diff returns
  // hasPrior:false and the dashboard shows the first-snapshot copy.
  const diff = useMemo(() => {
    if (!stored.current) return null;
    // Reconstruct a minimal "prior profile" from the compacted history entry
    // — we only need its edges + leaks signature lists for diffProfile to
    // work. Map the signature lists into entry-shaped stubs so diffProfile's
    // signature lookup behaves identically to a full prior profile.
    const priorSnapshot = (stored.history || [])[0];
    if (!priorSnapshot) return diffProfile(null, stored.current);
    const stubProfile = {
      generatedAt: priorSnapshot.generatedAt,
      edges: (priorSnapshot.edgeSignatures || []).map(s => ({ signature: s })),
      leaks: (priorSnapshot.leakSignatures || []).map(s => ({ signature: s })),
    };
    return diffProfile(stubProfile, stored.current);
  }, [stored]);

  return {
    profile: stored.current,
    history: stored.history,
    diff,
    isLoading,
    error,
    hasInitialRead: hasInitialReadRef.current,
    rebuild,
    clearMemory,
    // Exposed for the dashboard's "scope coverage" stat
    setupCount: setupsToAnalyze.length,
    atmCount: atmsToAnalyze.length,
    accountCount: accountsToAnalyze.length,
  };
};

export default useCoachProfile;
